import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ApsPermitedScreen } from '../../../models/aps-permission/aps-permited-screen';
import { ChoosePlanBuyng } from '../../../models/choose-plan/choose-plan-buyng';
import { ClientData } from '../../../models/client-data/client-data';
import { ConstantsFordProtect } from '../../../models/constants/constants-ford-protect';
import { ContractSummary } from '../../../models/contract-summary/contract-summary';
import { ContractFull } from '../../../models/contract/contract-full';
import { Term } from '../../../models/contract/term';
import { TermGeneration } from '../../../models/contract/term-generation';
import { Dealer } from '../../../models/dealer/dealer';
import { DealerCountryDto } from '../../../models/dealer/dealer-country-dto';
import { FileType } from '../../../models/enum/file-type';
import { OperationCode } from '../../../models/enum/operation-code';
import { SaleChannel } from '../../../models/enum/sale-channel';
import { SalesTypeFordEnum } from '../../../models/enum/sales-type-ford';
import { StatusChassi } from '../../../models/enum/status-chassi';
import { ProductFull } from '../../../models/product/product-full';
import { VinValidate } from '../../../models/vin-validate/vin-validate';
import { ValidationErrorMapperService } from '../../../services/address/validation-error-mapper.service';
import { ApsPermissionService } from '../../../services/aps-permission/aps-permission.service';
import { ChoosePlanService } from '../../../services/choose-plan/choose-plan.service';
import { ClientDataService } from '../../../services/client-data/client-data.service';
import { ContractSummaryService } from '../../../services/contract-summary/contract-summary.service';
import { ContractService } from '../../../services/contract/contract.service';
import { DealerService } from '../../../services/dealer/dealer.service';
import { I18nBrokerService } from '../../../services/i18n/i18n-broker.service';
import { SystemCountryService } from '../../../services/system-country/system-country.service';
import { VehicleInformationService } from '../../../services/vehicle-information/vehicle-information.service';
import { nationalId } from 'src/assets/validations/cpf_cnpj_validation';

@Component({
  selector: 'app-legal-text-contract-maintenance-argentina',
  templateUrl: './legal-text-contract-maintenance-argentina.component.html',
  styleUrls: ['./legal-text-contract-maintenance-argentina.component.scss'],
  standalone: true,
  imports: [CommonModule, TranslateModule]
})
export class LegalTextContractMaintenanceArgentinaComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  blockedUi = false;
  isDownloadingContract = false;
  isContractCreated = false;
  contractIdDownload = 0;

  vinValidate!: VinValidate;
  clientData!: ClientData;
  contractSummary!: ContractSummary;
  saleChannel: SaleChannel;
  choosePlanBuyng: ChoosePlanBuyng;
  systemCountry!: string;
  completeName!: string;
  fullAddress!: string;
  fullAddressFormatted!: string;

  // dealer
  dealer!: Dealer;
  dealerCuitLoaded = false;
  dealerCountry!: DealerCountryDto;
  dealerName!: string;
  dealerFullAddress!: string;
  dealerFullAddressFormatted!: string;

  lang: string = "";

  constructor(

    private contractSummaryService: ContractSummaryService,
    private vehicleInformationService: VehicleInformationService,
    private clientDataService: ClientDataService,
    private route: ActivatedRoute,
    private router: Router,
    private systemCountryService: SystemCountryService,
    private choosePlanService: ChoosePlanService,
    private apsPermissionService: ApsPermissionService,
    private dealerService: DealerService,
    private messageService: MessageService,
    private translateService: TranslateService,
    private contractService: ContractService,
    private i18nBroker: I18nBrokerService,
    private validationErrorMapperService: ValidationErrorMapperService

  ) {

    this.choosePlanBuyng = new ChoosePlanBuyng;
    this.saleChannel = route.snapshot.paramMap.get('saleChannel') as SaleChannel;
    this.systemCountryService.setSystemCountry();
  }

  ngOnInit() {

    window.scrollTo(0, 0);

    this.vinValidate = this.vehicleInformationService.getAllChassiValidation();
    this.clientData = this.clientDataService.getClientData();
    this.contractSummary = this.contractSummaryService.getContractSummary();


    if (this.vinValidate === null || this.vinValidate.validated === StatusChassi.invalid ||
      this.clientDataService.getClientData() === null || this.clientData === null ||
      this.contractSummary === null || this.choosePlanService.getChoosePlanBuyng() === null) {
      this.router.navigate(['sales-channel', this.saleChannel], { skipLocationChange: true, replaceUrl: false });
    }

    this.apsPermissionService.permissionValidation(ApsPermitedScreen.screenSignature);

    if (this.choosePlanService.getChoosePlanBuyng() !== null) {
      this.choosePlanBuyng = this.choosePlanService.getChoosePlanBuyng();
    }

    this.systemCountry = this.systemCountryService.getSystemCountry();

    this.completeName = this.clientData.firstname + " " + this.clientData.lastname;

    if (this.clientData.complement.length > 0) {

      this.fullAddress = this.clientData.address + " N° " + this.clientData.number + ", " + this.clientData.complement + ", "
        + this.clientData.city + " - " + this.clientData.stateCode + " Código Postal: " + this.clientData.zip;
      this.fullAddressFormatted = this.clientData.address + " N° " + this.clientData.number + ", " + this.clientData.complement + ", "
        + this.clientData.city + " - " + this.clientData.stateCode + " <strong>Código Postal:</strong> " + this.clientData.zip;
    } else {

      this.fullAddress = this.clientData.address + " N° " + this.clientData.number + " - "
        + this.clientData.city + " - " + this.clientData.stateCode + " Código Postal: " + this.clientData.zip;
      this.fullAddressFormatted = this.clientData.address + " N° " + this.clientData.number + " - "
        + this.clientData.city + " - " + this.clientData.stateCode + " <strong>Código Postal:</strong> " + this.clientData.zip;
    }

    // dealer
    this.getDealerCountry();
    this.dealerService.getDealerWithCuitFromBigQuery(this.contractSummary.dealer).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (dealer: Dealer) => {
        if (!dealer || !dealer.nationalId?.trim()) {
          this.handleDealerCuitUnavailable();
          return;
        }

        this.dealer = dealer;
        this.dealerName = dealer.legalName;
        this.dealerFullAddress = dealer.physicalStreet + ", " + dealer.physicalCity + " - " + dealer.physicalState + ", Código Postal: " + dealer.physicalZipCode;
        this.dealerFullAddressFormatted = dealer.physicalStreet + ", " + dealer.physicalCity + " - " + dealer.physicalState + ", <strong>Código Postal:</strong> " + dealer.physicalZipCode;
        this.dealerCuitLoaded = true;
      },
      error: () => this.handleDealerCuitUnavailable()
    });

  }

  private handleDealerCuitUnavailable() {
    this.dealerCuitLoaded = false;
    this.messageService.add({
      severity: 'error',
      summary: this.translateService.instant('TRANSLATE.ERROR'),
      detail: this.translateService.instant('TRANSLATE.ERROR.DEALER.CUIT.UNAVAILABLE')
    });
  }

  buttonComeBack() {
    this.router.navigate(['contract-summary', this.saleChannel], { skipLocationChange: true, replaceUrl: false });
  }

  // Consulta no backend o país do dealer
  getDealerCountry() {

    this.dealerService.getDealerCountry()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: DealerCountryDto) => {
          this.dealerCountry = response;
        },
        error: (error: { status?: number }) => {

          switch (error.status) {

            case 401:

              this.messageService.add({
                severity: 'error',
                summary: this.translateService.instant('TRANSLATE.ERROR'),
                detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
              });
              break;

            case 400:
            case 404:
            case 500:
              // PARA ESSE CASO UMA MENSAGEM DE ERRO SERÁ IMPLEMENTADA 
              // this.messageService.add({
              //   severity: 'error',
              //   summary: this.translateService.instant('TRANSLATE.ERROR'),
              //   detail: this.translateService.instant('TRANSLATE.ERROR.NO.RESULTS.FOUND.FOR.DEALER.COUNTRY.SEARCH')
              // });
              break;

            default:

              this.messageService.add({
                severity: 'error',
                summary: this.translateService.instant('TRANSLATE.ERROR'),
                detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
              });
              break;
          }

        }
      });
  }

  /*
     * Recupera o pais do usuario logado pelo token de acesso;
     * Caso não encontre, recupera o país do SITECODE;
     * Em ambos os casos, faz o parse do país do dealer com a tradução oficial (pt-BR, es-CL, es-CO etc)
     */
  setDealerLanguage() {
    if (this.systemCountry !== null) {
      this.lang = this.i18nBroker.getDealerLanguageByCountry(this.systemCountry);
    } else {
      this.lang = this.i18nBroker.getDealerLanguageByCountry(this.dealerCountry.countryAlternative);
    }
    return this.lang;
  }

  contractStartDate(): Date {

    let date = new Date();
    if (this.vinValidate.warrantyStartDate !== null) {
      date = this.vinValidate.warrantyStartDate;
    }
    return date;
  }

  get verifyingOverTwelveMonths(): boolean {

    const today = new Date();
    let startDate = new Date();
    if (this.vinValidate.warrantyStartDate !== null) {
      startDate = new Date(this.vinValidate.warrantyStartDate);
    }

    startDate.setFullYear(startDate.getFullYear() + 1);

    return today > startDate;
  }

  saveContractButton() {
    if (!this.dealerCuitLoaded) {
      this.handleDealerCuitUnavailable();
      return;
    }

    this.blockedUi = true;

    this.contractSummaryService
      .save(this.setContractFull(), this.setDealerLanguage())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: ContractFull) => {

          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('TRANSLATE.SUCCESS'),
            detail: this.translateService.instant('TRANSLATE.CONTRACT.CREATED.SUCCESSFULL')
          });

          this.vinValidate = new VinValidate();
          this.choosePlanBuyng = new ChoosePlanBuyng();
          this.clientData = new ClientData();
          this.contractSummary = new ContractSummary();

          this.contractIdDownload = response.contractId;
          this.isContractCreated = true;
          this.blockedUi = false;
          this.lang = "";

        },
        error: (error: { status?: number; apsRetryExhausted?: boolean }) => {

          this.blockedUi = false;
          this.isContractCreated = false;

          const friendlyError = this.contractSummaryService.getContractSaveFriendlyError(error);
          if (friendlyError) {
            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant('TRANSLATE.ERROR'),
              detail: friendlyError
            });
            return;
          }

          if (this.isApsRetryExhausted(error)) {
            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant('TRANSLATE.ERROR'),
              detail: 'Falha temporaria de autorizacao APS. A tentativa automatica nao conseguiu concluir. Tente novamente em instantes.'
            });
            return;
          }

          switch (error.status) {

            case 401:

              this.messageService.add({
                severity: 'error',
                summary: this.translateService.instant('TRANSLATE.ERROR'),
                detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
              });
              break;

            case 400:
            case 404:
            case 500:
              {
                const mappedError = this.validationErrorMapperService.mapBackendErrorToI18nKey(error);
                this.messageService.add({
                  severity: 'error',
                  summary: this.translateService.instant('TRANSLATE.ERROR'),
                  detail: this.translateService.instant(mappedError.key)
                });
                break;
              }

            default:

              this.messageService.add({
                severity: 'error',
                summary: this.translateService.instant('TRANSLATE.ERROR'),
                detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
              });
              break;
          }

        }
      });
  }

  private isApsRetryExhausted(error: unknown): boolean {
    return Boolean((error as { apsRetryExhausted?: boolean })?.apsRetryExhausted);
  }

  private setContractFull(): ContractFull {

    const contractFull = new ContractFull();
    contractFull.country = this.systemCountry;
    contractFull.operation = OperationCode.CAR;
    contractFull.status = ConstantsFordProtect.CONTRACT_ATIVO_CODE;
    contractFull.user = ConstantsFordProtect.EMISSION_USER;
    contractFull.saleDate = new Date();
    contractFull.startDate = this.contractStartDate();
    contractFull.saleType = SalesTypeFordEnum.RETAIL_CODE;
    contractFull.finalPrice = this.choosePlanBuyng.totalPrice;

    if (this.vinValidate.odometro !== null) {
      contractFull.odometerStart = this.vinValidate.odometro;
    } else {
      contractFull.odometerStart = 1;
    }

    contractFull.vin.vin = this.vinValidate.vin;
    contractFull.vin.cvvgDate = this.contractStartDate();
    contractFull.vin.factoryWarrantyEndDate = null;

    contractFull.vin.idVinCategoryVersion = this.vinValidate.idVinCategoryVersion;

    contractFull.payments.method = "DEALER";
    contractFull.payments.modality = this.contractSummary.formaPayment.value;
    contractFull.payments.parcels = this.contractSummary.prazoPayment.value;
    contractFull.payments.totalValue = this.choosePlanBuyng.totalPrice;

    contractFull.files.type = FileType.CONTRACT_SUCCESS_DEALER_MAINTENANCE_ARGENTINA;
    contractFull.files.name = FileType.CONTRACT_SUCCESS_DEALER_MAINTENANCE_ARGENTINA_NAME;
    contractFull.files.description = FileType.CONTRACT_SUCCESS_DEALER_MAINTENANCE_ARGENTINA_DESCRIPTION;
    contractFull.files.fileReference = FileType.CONTRACT_SUCCESS_DEALER_MAINTENANCE_ARGENTINA.toString();
    contractFull.files.file = "TEXTO PARA TESTE DO ARQUIVO - SERÁ MELHORADO FUTURAMENTE";

    contractFull.dealers.code = this.contractSummary.dealer.dealerCode;
    contractFull.dealers.pAndAC = this.contractSummary.dealer.pandAC;
    contractFull.dealers.divisionCode = this.contractSummary.dealer.divisionCode;
    contractFull.dealers.oldRegionCode = this.contractSummary.dealer.oldRegionCode;
    contractFull.dealers.geoSalesCode = this.contractSummary.dealer.geoSalesCode;
    contractFull.dealers.subCode = this.contractSummary.dealer.dealerSubCode;
    contractFull.dealers.name = this.contractSummary.dealer.dealerName;
    contractFull.dealers.email = this.contractSummary.dealer.dealerEmail;
    contractFull.dealers.phone = this.contractSummary.dealer.dealerPhone;

    contractFull.sellers.code = this.contractSummary.dealerSeller.sellerCode;
    contractFull.sellers.name = this.contractSummary.dealerSeller.name;
    contractFull.sellers.phone = this.contractSummary.dealerSeller.phone;
    contractFull.sellers.email = this.contractSummary.dealerSeller.email;
    contractFull.sellers.document = this.contractSummary.dealerSeller.document;
    contractFull.sellers.birthday = this.contractSummary.dealerSeller.birthday;

    contractFull.customers.country = this.clientData.countryCode;
    contractFull.customers.documentType = nationalId(this.clientData.nationalId, this.systemCountry);
    console.log('Document Type:', contractFull.customers.documentType);
    contractFull.customers.documentNumber = this.clientData.nationalId;
    contractFull.customers.name = this.clientData.firstname;
    contractFull.customers.phone = this.clientData.phone1;
    contractFull.customers.email = this.clientData.email1;
    contractFull.customers.lastName = this.clientData.lastname;
    contractFull.customers.cep = this.clientData.zip;
    contractFull.customers.streetName = this.clientData.address;
    contractFull.customers.streetNumber = this.clientData.number;
    contractFull.customers.streetComplement = this.clientData.complement;
    contractFull.customers.district = this.clientData.district;
    contractFull.customers.state = this.clientData.stateCode;
    contractFull.customers.city = this.clientData.city;
    contractFull.customers.customerSocialName = this.clientData.customerSocialName;

    if (this.choosePlanBuyng.extension.coverageCategoryId > 0 && this.choosePlanBuyng.extension.productId > 0) {
      const productFullExtension = new ProductFull();
      productFullExtension.coverageId = this.choosePlanBuyng.extension.coverageCategoryId;
      productFullExtension.productId = this.choosePlanBuyng.extension.productId;
      productFullExtension.productPolicyId = this.choosePlanBuyng.extension.productPolicyKey;
      productFullExtension.priceBookId = this.choosePlanBuyng.extension.pricebookKey;
      productFullExtension.priceBookId = this.choosePlanBuyng.extension.pricebookKey;
      contractFull.products.push(productFullExtension);
    }

    if (this.choosePlanBuyng.maintenance.coverageCategoryId > 0 && this.choosePlanBuyng.maintenance.productId > 0) {
      const productFullMaintenance = new ProductFull();
      productFullMaintenance.coverageId = this.choosePlanBuyng.maintenance.coverageCategoryId;
      productFullMaintenance.productId = this.choosePlanBuyng.maintenance.productId;
      productFullMaintenance.productPolicyId = this.choosePlanBuyng.extension.productPolicyKey;
      productFullMaintenance.priceBookId = this.choosePlanBuyng.extension.pricebookKey;
      productFullMaintenance.productPolicyId = this.choosePlanBuyng.maintenance.productPolicyKey;
      productFullMaintenance.priceBookId = this.choosePlanBuyng.maintenance.pricebookKey;
      contractFull.products.push(productFullMaintenance);
    }

    if (this.choosePlanBuyng.part.coverageCategoryId > 0 && this.choosePlanBuyng.part.productId > 0) {
      const productFullPart = new ProductFull();
      productFullPart.coverageId = this.choosePlanBuyng.part.coverageCategoryId;
      productFullPart.productId = this.choosePlanBuyng.part.productId;
      productFullPart.productPolicyId = this.choosePlanBuyng.extension.productPolicyKey;
      productFullPart.priceBookId = this.choosePlanBuyng.extension.pricebookKey;
      productFullPart.productPolicyId = this.choosePlanBuyng.part.productPolicyKey;
      productFullPart.priceBookId = this.choosePlanBuyng.part.pricebookKey;
      contractFull.products.push(productFullPart);
    }

    return contractFull;
  }

  downloadPDF() {

    if (this.isDownloadingContract) {
      return;
    }

    this.isDownloadingContract = true;
    this.contractService.searchByContractIdAndFileType(
      this.contractIdDownload,
      [FileType.CONTRACT_SUCCESS_DEALER_MAINTENANCE_ARGENTINA as number]
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (term: Term | null) => {
          if (!term?.fileId) {
            this.isDownloadingContract = false;
            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant('TRANSLATE.ERROR'),
              detail: this.translateService.instant('TRANSLATE.FILE.NOT.FOUND')
            });
            return;
          }

          const termGeneration = new TermGeneration(this.contractIdDownload, term.fileId);

          this.contractService.createFileTerm(termGeneration)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (blob) => {
                this.forceDownload(blob, FileType.CONTRACT_SUCCESS_DEALER_MAINTENANCE_ARGENTINA_NAME);
                this.isDownloadingContract = false;
              },
              error: (error: { status?: number }) => {
                this.isDownloadingContract = false;
                this.handleDownloadError(error);
              }
            });
        },
        error: (error: { status?: number }) => {
          this.isDownloadingContract = false;
          this.handleDownloadError(error);
        }
      });
  }

  private handleDownloadError(error: { status?: number }): void {
    switch (error.status) {

      case 401:

        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('TRANSLATE.ERROR'),
          detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
        });
        break;

      case 400:
      case 404:
      case 500:

        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('TRANSLATE.ERROR'),
          detail: this.translateService.instant('TRANSLATE.FILE.NOT.FOUND')
        });
        break;

      default:

        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('TRANSLATE.ERROR'),
          detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
        });
        break;
    }
  }

  private forceDownload(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    link.click();
    window.URL.revokeObjectURL(url);
  }

}
